import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { useDocumentAnalysis } from "./extraction/useDocumentAnalysis";
import { ProgressTimeline } from "./extraction/ProgressTimeline";
import { ProductsSection } from "./extraction/ProductsSection";
import { AudiencesSection } from "./extraction/AudiencesSection";
import { BrandVoiceSection } from "./extraction/BrandVoiceSection";
import type {
  ExtractedAudience,
  ExtractedBrandVoice,
  ExtractedProduct,
  SavedAnalysis,
  ToneValue,
} from "./extraction/types";

const errMessage = (e: unknown): string =>
  e instanceof Error ? e.message : String(e);

export function DocumentExtractionPanel({
  documentId,
  autoStart = true,
  savedAnalysis = null,
  analyzedAt = null,
  canReanalyze = true,
  lockReason = null,
}: {
  documentId: number;
  autoStart?: boolean;
  savedAnalysis?: SavedAnalysis | null;
  analyzedAt?: string | null;
  canReanalyze?: boolean;
  lockReason?: string | null;
}) {
  const utils = trpc.useUtils();
  const { data, steps, running, runAnalysis } = useDocumentAnalysis({
    documentId,
    savedAnalysis,
    autoStart,
    canReanalyze,
  });

  const [appliedProducts, setAppliedProducts] = useState<Set<number>>(new Set());
  const [appliedAudiences, setAppliedAudiences] = useState<Set<number>>(new Set());
  const [discardedProducts, setDiscardedProducts] = useState<Set<number>>(new Set());
  const [discardedAudiences, setDiscardedAudiences] = useState<Set<number>>(new Set());
  const [brandVoiceApplied, setBrandVoiceApplied] = useState(false);
  const [brandVoiceDiscarded, setBrandVoiceDiscarded] = useState(false);

  const { data: existingProducts = [] } = trpc.aiEmail.listProducts.useQuery();
  const { data: existingAudiences = [] } = trpc.aiEmail.listAudiences.useQuery();
  const { data: existingVoice } = trpc.aiEmail.getBrandVoice.useQuery();

  const createProductMut = trpc.aiEmail.createProduct.useMutation({
    onSuccess: () => {
      utils.aiEmail.listProducts.invalidate();
      toast.success("Produto criado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar produto")),
  });
  const updateProductMut = trpc.aiEmail.updateProduct.useMutation({
    onSuccess: () => {
      utils.aiEmail.listProducts.invalidate();
      toast.success("Produto atualizado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar produto")),
  });
  const createAudienceMut = trpc.aiEmail.createAudience.useMutation({
    onSuccess: () => {
      utils.aiEmail.listAudiences.invalidate();
      toast.success("Público-alvo criado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar público")),
  });
  const updateAudienceMut = trpc.aiEmail.updateAudience.useMutation({
    onSuccess: () => {
      utils.aiEmail.listAudiences.invalidate();
      toast.success("Público-alvo atualizado!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar público")),
  });
  const mergeBrandVoiceMut = trpc.aiEmail.mergeBrandVoice.useMutation({
    onSuccess: () => {
      utils.aiEmail.getBrandVoice.invalidate();
      toast.success("Voz da marca enriquecida!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao mesclar voz")),
  });

  function findExistingProduct(name: string) {
    const norm = name.trim().toLowerCase();
    return existingProducts.find((p) => p.name.trim().toLowerCase() === norm);
  }
  function findExistingAudience(name: string) {
    const norm = name.trim().toLowerCase();
    return existingAudiences.find((a) => a.name.trim().toLowerCase() === norm);
  }

  function applyProduct(idx: number, product: ExtractedProduct) {
    const existing = findExistingProduct(product.name);
    const payload = {
      name: product.name,
      description: product.description ?? undefined,
      valueProposition: product.valueProposition ?? undefined,
      differentials: product.differentials?.length ? product.differentials : undefined,
      price: product.price ?? undefined,
      category: product.category ?? undefined,
    };
    const onSuccess = () => setAppliedProducts((prev) => new Set(prev).add(idx));
    if (existing) {
      updateProductMut.mutate({ id: existing.id, ...payload }, { onSuccess });
    } else {
      createProductMut.mutate(payload, { onSuccess });
    }
  }

  function applyAudience(idx: number, audience: ExtractedAudience) {
    const existing = findExistingAudience(audience.name);
    const payload = {
      name: audience.name,
      description: audience.description ?? undefined,
      painPoints: audience.painPoints?.length ? audience.painPoints : undefined,
      desires: audience.desires?.length ? audience.desires : undefined,
      commonObjections: audience.commonObjections?.length ? audience.commonObjections : undefined,
      preferredTone: audience.preferredTone ?? undefined,
      ageRange: audience.ageRange ?? undefined,
      role: audience.role ?? undefined,
    };
    const onSuccess = () => setAppliedAudiences((prev) => new Set(prev).add(idx));
    if (existing) {
      updateAudienceMut.mutate({ id: existing.id, ...payload }, { onSuccess });
    } else {
      createAudienceMut.mutate(payload, { onSuccess });
    }
  }

  function applyBrandVoice(voice: ExtractedBrandVoice) {
    if (!voice.brandName?.trim() && !existingVoice?.brandName) {
      toast.error("Nome da marca não detectado. Edite manualmente na aba Voz da Marca.");
      return;
    }
    mergeBrandVoiceMut.mutate(
      {
        brandName: voice.brandName?.trim() || undefined,
        toneOfVoice: (voice.suggestedTone as ToneValue | undefined) ?? undefined,
        wordsToUse: voice.wordsToUse?.length ? voice.wordsToUse : undefined,
        wordsToAvoid: voice.wordsToAvoid?.length ? voice.wordsToAvoid : undefined,
        slogan: voice.slogan?.trim() || undefined,
        catchPhrases: voice.catchPhrases?.length ? voice.catchPhrases : undefined,
        emojis: voice.emojis?.length ? voice.emojis : undefined,
        sampleText: voice.sampleText ?? undefined,
      },
      { onSuccess: () => setBrandVoiceApplied(true) },
    );
  }

  async function reanalyze() {
    setAppliedProducts(new Set());
    setAppliedAudiences(new Set());
    setDiscardedProducts(new Set());
    setDiscardedAudiences(new Set());
    setBrandVoiceApplied(false);
    setBrandVoiceDiscarded(false);
    try {
      await runAnalysis();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  const allIdle = steps.products.status === "idle" && steps.audiences.status === "idle" && steps.brandVoice.status === "idle";
  const hasResults = steps.products.status === "done" || steps.audiences.status === "done" || steps.brandVoice.status === "done";

  if (allIdle) {
    return (
      <div className="border-t pt-3 mt-3 space-y-2">
        {!canReanalyze && lockReason && (
          <p className="text-[11px] text-muted-foreground">{lockReason}</p>
        )}
        <Button size="sm" variant="outline" onClick={reanalyze} disabled={running || !canReanalyze}>
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Analisar com IA — extrair produtos, público e voz
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 mt-3 space-y-4">
      <ProgressTimeline steps={steps} />

      {data.wasTruncated && (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            <strong>Documento truncado:</strong> a análise considerou apenas a parte inicial do documento.
            Para análises mais completas, divida o conteúdo em documentos menores.
          </p>
        </div>
      )}

      {data.productsSummary && (
        <div className="text-xs text-muted-foreground italic">
          <strong className="not-italic text-foreground">Resumo:</strong> {data.productsSummary}
        </div>
      )}

      <ProductsSection
        products={data.products}
        existingProducts={existingProducts}
        appliedIdx={appliedProducts}
        discardedIdx={discardedProducts}
        isPending={createProductMut.isPending || updateProductMut.isPending}
        onApply={applyProduct}
        onDiscard={(idx) => setDiscardedProducts((prev) => new Set(prev).add(idx))}
      />

      <AudiencesSection
        audiences={data.audiences}
        existingAudiences={existingAudiences}
        appliedIdx={appliedAudiences}
        discardedIdx={discardedAudiences}
        isPending={createAudienceMut.isPending || updateAudienceMut.isPending}
        onApply={applyAudience}
        onDiscard={(idx) => setDiscardedAudiences((prev) => new Set(prev).add(idx))}
      />

      <BrandVoiceSection
        brandVoice={data.brandVoice}
        existingBrandName={existingVoice?.brandName}
        applied={brandVoiceApplied}
        discarded={brandVoiceDiscarded}
        isPending={mergeBrandVoiceMut.isPending}
        onApply={applyBrandVoice}
        onDiscard={() => setBrandVoiceDiscarded(true)}
      />

      {!running && hasResults && data.products.length === 0 && data.audiences.length === 0 && !data.brandVoice && (
        <div className="text-xs text-muted-foreground italic">
          A IA não detectou produtos, públicos ou voz da marca neste documento.
        </div>
      )}

      {!running && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button size="sm" variant="ghost" onClick={reanalyze} disabled={!canReanalyze || running}>
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            Reanalizar
          </Button>
          {!canReanalyze && lockReason && (
            <p className="text-[11px] text-muted-foreground">{lockReason}</p>
          )}
          {analyzedAt && (
            <p className="text-[11px] text-muted-foreground">
              Analisado em {new Date(analyzedAt).toLocaleString("pt-BR")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
